import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

export default function Privacy() {
  const navigate = useNavigate();
  const lastUpdated = "2026年5月21日";

  return (
    <div
      className="bg-slate-50 dark:bg-[#020617] text-slate-900 dark:text-slate-100"
      style={{ minHeight: "100dvh" }}
    >
      <header className="pt-safe px-4 pb-3 flex items-center gap-3 border-b border-slate-100 dark:border-slate-800 bg-white/80 dark:bg-slate-900/80 backdrop-blur-sm sticky top-0 z-10">
        <button
          onClick={() => navigate(-1)}
          className="p-2.5 rounded-full active:bg-slate-200 dark:active:bg-slate-800 transition-colors"
        >
          <ArrowLeft size={22} className="text-slate-600 dark:text-slate-400" />
        </button>
        <h1 className="text-lg font-black">プライバシーポリシー</h1>
      </header>

      <div className="max-w-2xl mx-auto px-5 py-8 space-y-8 pb-16">
        <p className="text-sm text-slate-400">最終更新日：{lastUpdated}</p>

        <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
          本プライバシーポリシーは、locomoco（以下「本サービス」）がユーザーの個人情報をどのように収集・利用・保護するかを説明するものです。
        </p>

        <LegalSection title="1. 収集する情報">
          <p>本サービスは以下の情報を収集します。</p>
          <SubList
            items={[
              "【プロフィール情報】ユーザー名（任意）",
              "【学習データ】単語ごとのスワイプ結果（「また後で」「知っている」「もう完璧」）、学習セッション履歴、定着スコア、ストリーク（連続学習日数）",
              "【設定情報】1日の区切り時刻、ユーザー設定",
              "【端末・利用情報】アクセス日時、利用端末の種別（ログ）",
            ]}
          />
          <p className="mt-2">
            本サービスは現在ログイン機能を提供しておらず、メールアドレス等の個人を特定する情報は収集しません。
          </p>
        </LegalSection>

        <LegalSection title="2. 情報の利用目的">
          <SubList
            items={[
              "学習進捗の記録および表示",
              "スペーシング効果に基づく出題アルゴリズムの実行",
              "ストリーク・ゲーミフィケーション機能の提供",
              "本サービスの改善・開発のための統計的分析（個人を特定しない形式）",
              "重要なお知らせ・サービス変更の通知",
            ]}
          />
        </LegalSection>

        <LegalSection title="3. 第三者への提供">
          <p>本サービスは、以下の場合を除き、ユーザーの個人情報を第三者に提供しません。</p>
          <SubList
            items={[
              "ユーザーの同意がある場合",
              "法令に基づく場合",
              "人の生命・身体・財産の保護のために必要がある場合",
            ]}
          />
        </LegalSection>

        <LegalSection title="4. 利用しているサービス・技術">
          <p>本サービスは以下の外部サービスを利用しています。</p>
          <div className="mt-3 space-y-3">
            <div className="bg-white dark:bg-slate-800 rounded-xl p-4 border border-slate-100 dark:border-slate-700">
              <p className="font-bold text-sm text-slate-900 dark:text-white">
                Cloudflare（ホスティング・データベース）
              </p>
              <p className="text-xs text-slate-500 mt-1">
                アプリのホスティング・データ保存に使用。
                <a
                  href="https://www.cloudflare.com/privacypolicy/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-500 ml-1 underline"
                >
                  プライバシーポリシー
                </a>
              </p>
            </div>
          </div>
        </LegalSection>

        <LegalSection title="5. データの保存期間">
          <SubList
            items={[
              "学習データ・設定情報：アカウント削除を実行するまで保持",
            ]}
          />
        </LegalSection>

        <LegalSection title="6. ユーザーの権利">
          <p>ユーザーは以下の権利を有します。</p>
          <SubList
            items={[
              "保有する個人情報の開示を求める権利",
              "個人情報の訂正・追加・削除を求める権利",
              "アカウントの削除（設定画面の「アカウントを削除する」から実行可能）",
            ]}
          />
        </LegalSection>

        <LegalSection title="7. Cookie・ローカルストレージ">
          <p>
            本サービスは、学習セッションの一時保存および表示設定の保持のためにブラウザの
            ローカルストレージを使用します。これらはいつでもブラウザの設定から削除できます。
          </p>
        </LegalSection>

        <LegalSection title="8. 未成年者">
          <p>
            本サービスは13歳以上を対象としています。13歳未満のお子様の個人情報を故意に収集することはありません。
          </p>
        </LegalSection>

        <LegalSection title="9. プライバシーポリシーの変更">
          <p>
            本ポリシーの内容は、法令その他の本ポリシーに関連する規範の変化や、
            本サービスの内容変更等に伴い、変更されることがあります。
            変更後のポリシーは本ページに掲示された時点から効力を生じるものとします。
          </p>
        </LegalSection>

        <LegalSection title="10. お問い合わせ">
          <p>
            本ポリシーに関するご質問・ご意見は、設定画面の「お問い合わせ」からご連絡ください。
          </p>
        </LegalSection>

        <p className="text-xs text-slate-400 text-center pt-4">以上</p>
      </div>
    </div>
  );
}

function LegalSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h2 className="text-base font-black text-slate-900 dark:text-white mb-2">{title}</h2>
      <div className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed space-y-2">
        {children}
      </div>
    </section>
  );
}

function SubList({ items }: { items: string[] }) {
  return (
    <ul className="ml-4 mt-1 space-y-1 list-disc">
      {items.map((item, i) => (
        <li key={i}>{item}</li>
      ))}
    </ul>
  );
}
